import {FlatTreeControl} from '@angular/cdk/tree';
import {
  Component,
  EventEmitter,
  Injectable,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  HostListener
} from '@angular/core';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {BehaviorSubject, forkJoin, Observable, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, take, takeUntil} from 'rxjs/operators';
import {ProductDTO} from '../../../../models/ProductDTO';
import {ProductBenefitLimitLinkDTO} from '../../../../models/ProductBenefitLimitLinkDTO';
import {MatDialog} from '@angular/material/dialog';
import {BenefitService} from '../../../../services/benefit.service';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {LimitService} from '../../../../services/limit.service';
import {SharedService} from '../../../../../common/services/shared-service';
import {EntRequestBuilder} from '../../../../services/ent-request-builder';
import {PagedResponseDTO} from '../../../../../common/models/PagedResponseDTO';
import {LimitDTO} from '../../../../models/LimitDTO';
import {BenefitDTO} from '../../../../models/BenefitDTO';
import {Common, formatNumber} from '../../../../../common/util/Util';
import {
  ConfirmationDialogComponent
} from '../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {CustomValidator} from '../../../../../common';
import {AddBenefitDialogComponent} from '../../../dialogs/add-benefit-dialog/add-benefit-dialog.component';
import {AddLimitDialogComponent} from '../../../dialogs/add-limit-dialog/add-limit-dialog.component';
import {SharedFormService} from '../../../../../common/services/shared-form-service';

/**
 * Node for to-do item
 */
export class ItemNode {
  children: ItemNode[];
  item: string;
  benefitId?: bigint;
  limitId?: bigint;
}

/** Flat to-do item node with expandable and level information */
export class ItemFlatNode {
  item: string;
  level: number;
  expandable: boolean;
}

/**
 * PBL Service database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable({
  providedIn: 'root'
})
export class PBLTreeService {
  dataChange = new BehaviorSubject<ItemNode[]>([]);

  constructor() {
    this.initialize();
  }

  get data(): ItemNode[] {
    return this.dataChange.value;
  }

  dataChangeAsObservable(): Observable<Array<ItemNode>> {
    return this.dataChange.asObservable();
  }

  initialize(treeData = {}) {
    // Build the tree nodes from Json object. The result is a list of `ItemNode` with nested
    //     file node as children.
    const data = this.buildFileTree(treeData, 0);

    // Notify the change.
    this.dataChange.next(data);
  }

  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `ItemNode`.
   */
  buildFileTree(obj: { [key: string]: any }, level: number): ItemNode[] {
    return Object.keys(obj)
      .reduce<ItemNode[]>((accumulator, key) => {
        const value = obj[key];
        const node = new ItemNode();
        node.item = key;

        // Important to code BenefitId and LimitId in each NodeItem
        if (level === 2) {
          node.benefitId = obj[key].benefitId;
          node.limitId = obj[key].limitId;
          delete obj[key].benefitId;
          delete obj[key].limitId;
        }

        if (value != null) {
          if (typeof value === 'object') {
            node.children = this.buildFileTree(value, level + 1);
          } else {
            node.item = value;
          }
        }

        return accumulator.concat(node);
      }, []);
  }

  /** Add an item to to-do list */
  insertItem(parent: ItemNode, node: ItemFlatNode, name: string) {
    if (parent.children) {
      const newChild = new ItemNode();
      newChild.item = name;
      newChild.children = [];
      parent.children.unshift(newChild);
      this.dataChange.next(this.data);
    }
  }

  updateItem(node: ItemNode, flatNode: ItemFlatNode, data: any) {
    const {name, benefitId, limitId} = data;
    node.item = name;
    node.benefitId = benefitId;
    node.limitId = limitId;
    this.dataChange.next(this.data);
  }

  deleteItem(parent: ItemNode, name: string): void {
    if (parent.children) {
      parent.children = parent.children.filter(c => c.item !== name);
      this.dataChange.next(this.data);
    }
  }
}

enum TreeLevel {
  ProductLevel = 0,  // depth=0
  BenefitLevel = 1,  // depth=1
  LimitLevel = 2,    // depth=2
}

enum Controls {
  ParentBenefit = 'parentBenefit',
  FirstAlternativeBenefit = 'firstAlternativeBenefit',
  SecondAlternativeBenefit = 'secondAlternativeBenefit',
  CoPaymentPercentage = 'coPaymentPercentage',
  PercentageOfPayerRate = 'percentageOfPayerRate',
  Deductible = 'deductible',
  WaitingPeriod = 'waitingPeriod',
  PecWaitingPeriod = 'pecWaitingPeriod',
  StartDate = 'startDate',
  EndDate = 'endDate',
}

/*
  - Used as Tooltip component Input names
 */
enum ToolTipControls {
  ParentBenefit = 'Parent Benefit',
  FirstAlternativeBenefit = 'First Alternative Benefit',
  SecondAlternativeBenefit = 'Second Alternative Benefit',
  CoPaymentPercentage = 'Co-payment %',
  PercentageOfPayerRate = 'Percentage of Payer Rate',
  Deductible = 'Deductible Amount',
  WaitingPeriod = 'General Waiting Period',
  PecWaitingPeriod = 'PEC Waiting Period',
  StartDate = 'Start Date-Benefit',
  EndDate = 'End Date-Benefit',
}

enum ModeType {
  Add = 'Add',
  Delete = 'Delete',
}

@Component({
  selector: 'app-tree-benefits-and-limits',
  templateUrl: './tree-benefits-and-limits.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_add-view-edit-styles.scss'
  ],
})
export class TreeBenefitsAndLimitsComponent implements OnChanges, OnInit, OnDestroy {
  readonly controls = Controls;
  readonly benefitByNameOrByBenefitCodeTextChanged$ = new Subject<string>();
  readonly limitTextChanged$ = new Subject<string>();
  readonly destroyed$ = new Subject();
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly toolTipControls = ToolTipControls;
  readonly treeLevel = TreeLevel;
  readonly modeType = ModeType;
  readonly EMPTY_VALUE = 'EMPTY_VALUE';
  readonly PRIORITY_BENEFIT_CODES = ['OAB001'];
  readonly MIN_CHARS_LENGTH_AFTER_TOOLTIP_TO_VISIBLE = 100; // Random Value (Length is based on getBenefitDisplayNameForToolTip method)

  isParentBenefitRequired = true; // Handles Special Case
  filteredBenefits: BenefitDTO[];
  filteredLimits: LimitDTO[];
  productBenefitLimitLinkDTOList: ProductBenefitLimitLinkDTO[];
  benefitLimitsFormGroup: FormGroup;

  selectedLimit: LimitDTO;
  selectedBenefit: BenefitDTO;
  selectedParentBenefit: BenefitDTO;
  selectedFirstAlternativeBenefit: BenefitDTO;
  selectedSecondAlternativeBenefit: BenefitDTO;
  isScrollToTop: boolean;
  minimumTopPosToStartScrolling = 100;

  private _isAddingNewBenefit: boolean;
  private _isAddingNewLimit: boolean;
  private _showConfigurationSection = false; // Default hidden in Add/Edit -> Shown on limit selection.
  private _isBenefitWithoutLimit: boolean;
  private _benefitsDTOHashMap = new Map<bigint, BenefitDTO>();
  private _limitsDTOHashMap = new Map<bigint, LimitDTO>();

  @Input() isEditMode: boolean;
  @Input() productDTO: ProductDTO;
  @Output() ProductBenefitLimitLinkDTOListChange: EventEmitter<ProductBenefitLimitLinkDTO[]> = new EventEmitter<ProductBenefitLimitLinkDTO[]>();
  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<ItemFlatNode, ItemNode>();
  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<ItemNode, ItemFlatNode>();
  treeControl: FlatTreeControl<ItemFlatNode>;
  treeFlattener: MatTreeFlattener<ItemNode, ItemFlatNode>;
  dataSource: MatTreeFlatDataSource<ItemNode, ItemFlatNode>;

  @Output() isValid: EventEmitter<boolean> = new EventEmitter();

  private loggedInUserName: string;

  getLevel = (node: ItemFlatNode) => node.level;
  isExpandable = (node: ItemFlatNode) => node.expandable;
  getChildren = (node: ItemNode): ItemNode[] => node.children;
  hasChild = (_: number, _nodeData: ItemFlatNode) => _nodeData.expandable; // Imp!! check `transformer` method
  hasNoContentAndLevelBenefit = (_: number, _nodeData: ItemFlatNode) => {
    return _nodeData.item === this.EMPTY_VALUE && _nodeData.level === TreeLevel.BenefitLevel;
  }
  hasNoContentAndLevelLimit = (_: number, _nodeData: ItemFlatNode) => {
    return _nodeData.item === this.EMPTY_VALUE && _nodeData.level === TreeLevel.LimitLevel;
  }

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: ItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode =
      existingNode && existingNode.item === node.item ? existingNode : new ItemFlatNode();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.expandable = !!node.children && level !== 2; // Don't allow Limit to have "+" Icon;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }

  get isFormValid(): boolean {
    return !!this.benefitLimitsFormGroup?.valid;
  }

  get showConfigurationSection(): boolean {
    return this._showConfigurationSection;
  }

  set showConfigurationSection(value: boolean) {
    this._showConfigurationSection = value;
  }

  get isPBLLinkDTOListEmpty(): boolean {
    return this.productBenefitLimitLinkDTOList.length === 0;
  }

  get isAddingNewBenefit(): boolean {
    return this._isAddingNewBenefit;
  }

  set isAddingNewBenefit(value: boolean) {
    this._isAddingNewBenefit = value;
  }

  get isAddingNewLimit(): boolean {
    return this._isAddingNewLimit;
  }

  set isAddingNewLimit(value: boolean) {
    this._isAddingNewLimit = value;
  }

  get isBenefitWithoutLimit(): boolean {
    return this._isBenefitWithoutLimit;
  }

  set isBenefitWithoutLimit(value: boolean) {
    this._isBenefitWithoutLimit = value;
  }

  get isNotAllowedToSaveProductWhen(): boolean {
    return (
      this.isPBLLinkDTOListEmpty ||
      this.isBenefitWithoutLimit ||
      this.showConfigurationSection ||
      this.isAddingNewBenefit ||
      this.isAddingNewLimit
    );
  }

  get getMatTooltipText() {
    if (this.isPBLLinkDTOListEmpty || this.isBenefitWithoutLimit) {
      return 'Please ensure every product has at least 1 benefit, and every benefit has at least 1 limit';
    } else if (this.isFormValid || this.isAddingNewBenefit || this.showConfigurationSection) {
      return 'Please save the configuration fields for the selected benefit.';
    } else if (this.isAddingNewLimit) {
      return 'Please finish adding the limit, or cancel the operation by clicking trash️ icon';
    }
    return null;
  }


  @HostListener('window:scroll') checkScroll() {
    // Both window.pageYOffset and document.documentElement.scrollTop returns the same result in all the cases:
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop || 0;
    this.isScrollToTop = scrollPosition >= this.minimumTopPosToStartScrolling;
  }

  constructor(public dialog: MatDialog,
              private pblTreeService: PBLTreeService,
              private benefitService: BenefitService,
              private formBuilder: FormBuilder,
              private limitService: LimitService,
              private sharedService: SharedService,
              private sharedFormService: SharedFormService) {

    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this.getLevel,
      this.isExpandable,
      this.getChildren
    );
    this.treeControl = new FlatTreeControl<ItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    pblTreeService.dataChange.subscribe(data => {
      this.dataSource.data = data;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.isEditMode && this.productDTO) {
      const {productBenefitLimitLinkDTO} = this.productDTO;
      this.productBenefitLimitLinkDTOList = productBenefitLimitLinkDTO ? [...productBenefitLimitLinkDTO] : [];
      this.buildAndExpandTree(productBenefitLimitLinkDTO);

      const itemFound = this.productBenefitLimitLinkDTOList.find(item => {
        if (this.PRIORITY_BENEFIT_CODES.includes(item.benefit?.benefitCode)) {
          return item;
        }
      });

      if (itemFound) {
        this.setOrClearParentBenefitControlValidator(itemFound.benefit);
      }
      this.setBenefitLimitDTOHashmapValues(productBenefitLimitLinkDTO);
    } else {
      // Case: Add Benefit -> Navigate to Step back -> come forward
      // Instead of clearing the list build tree from the list

      if (this.productBenefitLimitLinkDTOList?.length) {
        this.buildAndExpandTree(this.productBenefitLimitLinkDTOList);
      } else {
        this.productBenefitLimitLinkDTOList = [];
      }
    }

    this.checkAndNotifyParentIfFormIsValid();
  }

  ngOnInit() {
    this.initSubscriptions();
    this.initFormGroup();
    this.getHelpInfo('Product');
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initSubscriptions() {

    this.sharedService.getLoggedInProfile$()
      .pipe(
        take(1)
      )
      .subscribe(() => {
        // Note: Won't be using profile obj instead invoke `getLoggedInUserName` as username that is already set in service.
        this.loggedInUserName = this.sharedService.getLoggedInUserName();
      });

    this.benefitByNameOrByBenefitCodeTextChanged$.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
      debounceTime(this.debounceTime)
    )
      .subscribe((searchValue: string) => {
          this.getFilteredListOfBenefits(searchValue);
        },
        error => console.log(error));

    this.limitTextChanged$.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
      debounceTime(this.debounceTime)
    )
      .subscribe((searchValue: string) => {
          this.getFilteredListOfLimits(searchValue);
        },
        error => console.log(error));
  }

  initFormGroup() {
    this.benefitLimitsFormGroup = this.formBuilder.group({
      [Controls.ParentBenefit]: this.formBuilder.control(null, [Validators.required]),
      [Controls.FirstAlternativeBenefit]: this.formBuilder.control(null),
      [Controls.SecondAlternativeBenefit]: this.formBuilder.control(null),
      [Controls.CoPaymentPercentage]: new FormControl(null, [Validators.min(0)]),
      [Controls.PercentageOfPayerRate]: this.formBuilder.control(null),
      [Controls.Deductible]: this.formBuilder.control(null, []),
      [Controls.WaitingPeriod]: this.formBuilder.control(null, [Validators.min(0), CustomValidator.wholeNumber]),
      [Controls.PecWaitingPeriod]: this.formBuilder.control(null, [Validators.min(0), CustomValidator.wholeNumber]),
      [Controls.StartDate]: this.formBuilder.control(null, [Validators.required]),
      [Controls.EndDate]: this.formBuilder.control(null, [Validators.required])
    });
  }

  getHelpInfo(componentName: string) {
    this.sharedService.getHelpInfo(componentName);
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  getTreeTooltip(node: ItemFlatNode, type: ModeType): string {
    if (type === ModeType.Add) {
      if (node.level === TreeLevel.ProductLevel) {
        return 'Add a benefit to the product';
      } else {
        return 'Add a limit to the benefit';
      }
    } else if (type === ModeType.Delete) {
      if (node.level === TreeLevel.BenefitLevel) {
        return 'Remove the benefit from the product';
      } else if (node.level === TreeLevel.LimitLevel) {
        return 'Remove the limit from the benefit';
      }
    }
  }

  openBenefitDialog() {
    const dialogRef = this.dialog.open(AddBenefitDialogComponent, {
      width: '50vw',
      data: {},
      disableClose: true
    });
  }

  openLimitDialog() {
    const dialogRef = this.dialog.open(AddLimitDialogComponent, {
      width: '35vw',
      data: {},
      disableClose: true
    });
  }

  getParentNode(node: ItemFlatNode): ItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  addNewItem(node: ItemFlatNode) {

    if (this.checkIsEmptyBenefitOrLimitAlreadyExists(node)) {
      return;
    }

    if (node.level === TreeLevel.ProductLevel) {
      this.isAddingNewBenefit = true; // Adding NEW benefit
      this.showConfigurationSection = true;
      this.resetState();

      // Default Start Date and End Date to the values selected in General step
      this.benefitLimitsFormGroup.patchValue({
        [Controls.StartDate]: this.productDTO?.startDate ? this.productDTO?.startDate : null,
        [Controls.EndDate]: this.productDTO?.endDate ? this.productDTO?.endDate : null,
      });

    } else if (node.level === TreeLevel.BenefitLevel) {
      this.isAddingNewLimit = true;
      this.showConfigurationSection = false; // Adding NEW Limit
    }

    if (node.level === TreeLevel.BenefitLevel && this.isAddingNewBenefit) {
      this.sharedService.openSnackBar(`Add and save benefit configuration before adding any limit `, 'Dismiss');
      return;
    }
    const parentNode = this.flatNodeMap.get(node);
    this.pblTreeService.insertItem(parentNode, node, this.EMPTY_VALUE);
    this.treeControl.expand(node);
    this.isBenefitWithoutLimit = parentNode && parentNode.children.length === 0;
  }

  onBenefitSelectionChanged(benefit: BenefitDTO, node: ItemFlatNode) {
    this.selectedBenefit = benefit;
    this._benefitsDTOHashMap.set(benefit.benefitId, benefit);
    const nestedNode = this.flatNodeMap.get(node);
    this.setOrClearParentBenefitControlValidator(benefit);
    this.pblTreeService.updateItem(nestedNode, node, {
      name: this.getBenefitDisplayName(benefit),
      benefitId: benefit.benefitId,
      limitId: null
    });
  }

  onLimitSelectionChanged(limit: LimitDTO, node: ItemFlatNode) {
    this.selectedLimit = limit;
    this._limitsDTOHashMap.set(limit.limitId, limit);
    const nestedNode = this.flatNodeMap.get(node);
    const parentNode = this.getParentNode(node); // Get Parent node (Benefit)
    const itemNode = this.flatNodeMap.get(parentNode); // Get ItemNode from Hashmap -> get ParentId


    let benefitDTO = this._benefitsDTOHashMap.get(itemNode.benefitId);
    const limitDTO = this._limitsDTOHashMap.get(limit.limitId);

    // ------------------------------------
    // FIX
    let benfitId;
    if (benefitDTO === undefined) {
      // Hack: but check if childrens exists and if any children does have benefitId use that one
      benfitId = itemNode.children.find(item => item?.benefitId)?.benefitId;

      if (benfitId) {
        benefitDTO = this._benefitsDTOHashMap.get(benfitId);
      } else {
        this._benefitsDTOHashMap.forEach((value, key) => {
          const benefitName = this.getBenefitDisplayName(value);
          if (parentNode.item.includes(benefitName)) {
            benfitId = key;
            benefitDTO = this._benefitsDTOHashMap.get(benfitId);
            return;
          }
        });
      }
    }

    let pblItemFound;
    if (benefitDTO) {
      pblItemFound = this.productBenefitLimitLinkDTOList?.find(item => item?.benefit?.benefitId === benefitDTO?.benefitId);
    } else {
      pblItemFound = this.productBenefitLimitLinkDTOList?.find(item => item?.benefit?.benefitId === benfitId);
    }
    // ------------------------------------

    if (pblItemFound) {
      const {benefit, limit, parentBenefit, firstAlternativeBenefit, secondAlternativeBenefit} = pblItemFound;
      this._benefitsDTOHashMap.set(benefit.benefitId, benefit);
      this.selectedBenefit = benefit;
      this.selectedLimit = limit;
      this.selectedParentBenefit = parentBenefit;
      this.selectedFirstAlternativeBenefit = firstAlternativeBenefit;
      this.selectedSecondAlternativeBenefit = secondAlternativeBenefit;
      this.setPBLConfigurations(pblItemFound);

      // Add NEW Limit Object in LIST
      const newProductBenefitLimitLinkDTO = this.getProductBenefitLimitLinkDTOPayload(benefitDTO, limitDTO);
      this.productBenefitLimitLinkDTOList.push(newProductBenefitLimitLinkDTO);
      this.isAddingNewLimit = false;
    }

    this.pblTreeService.updateItem(nestedNode, node, {
      name: this.getLimitDisplayName(limit),
      benefitId: itemNode.benefitId || benfitId,
      limitId: limit.limitId
    });
  }

  getBenefitDisplayName(benefit: BenefitDTO, withBold: boolean = true) {
    if (benefit && withBold) {
      return `<b>${benefit.benefitCode}:</b> <span class="truncate-benefit-name">${benefit.name}</span>`;
    } else {
      return `${benefit.benefitCode}: ${benefit.name}`;
    }
  }

  getBenefitDisplayNameForToolTip(node: ItemFlatNode): string {
    const {item} = node;
    if (item) {
      // Remove Class that applies truncation
      return item.replace('class="truncate-benefit-name"', '');
    }
    return null;
  }

  getLimitDisplayName(limit: LimitDTO) {
    if (limit) {
      return `${limit.name}: ${limit.value}`;
    }
    return null;
  }

  deleteItem(node: ItemFlatNode): void {
    if (node.level === TreeLevel.BenefitLevel) {
      if (node.item === this.EMPTY_VALUE) {
        this.deleteNode(node); // Case: Deleting "New" Benefit
      } else {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
          width: '40vw',
          data: 'Are you sure you want to delete the benefit? All of it\'s limits will be deleted too.'
        });
        dialogRef.afterClosed()
          .subscribe((isConfirmed) => {
            if (isConfirmed) {
              this.deleteNode(node);
            }
          });
      }
    } else if (node.level === TreeLevel.LimitLevel) {
      this.deleteNode(node); // Case: Deleting Limit
    }
  }

  onPBLConfigurationBenefitSelectionChanged(benefit: BenefitDTO, type: string) {
    switch (type) {
      case Controls.ParentBenefit:
        this.selectedParentBenefit = benefit;
        this.benefitLimitsFormGroup.get(this.controls.ParentBenefit)
          .patchValue(this.getBenefitDisplayName(benefit, false));
        break;
      case Controls.FirstAlternativeBenefit:
        this.selectedFirstAlternativeBenefit = benefit;
        this.benefitLimitsFormGroup.get(this.controls.FirstAlternativeBenefit)
          .patchValue(this.getBenefitDisplayName(benefit, false));
        break;
      case Controls.SecondAlternativeBenefit:
        this.selectedSecondAlternativeBenefit = benefit;
        this.benefitLimitsFormGroup.get(this.controls.SecondAlternativeBenefit)
          .patchValue(this.getBenefitDisplayName(benefit, false));
        break;
      default:
        break;
    }
  }

  clearParentBenefit() {
    this.benefitLimitsFormGroup.get(this.controls.ParentBenefit).reset();
    this.selectedParentBenefit = null;
  }

  clearFirstAlternativeBenefit() {
    this.benefitLimitsFormGroup.get(this.controls.FirstAlternativeBenefit).reset();
    this.selectedFirstAlternativeBenefit = null;
  }

  clearSecondAlternativeBenefit() {
    this.benefitLimitsFormGroup.get(this.controls.SecondAlternativeBenefit).reset();
    this.selectedSecondAlternativeBenefit = null;
  }

  resetFormGroup() {
    this.benefitLimitsFormGroup.reset();
  }

  onBlurParentBenefit() {
    const value = this.benefitLimitsFormGroup.get(Controls.ParentBenefit).value;
    if (!value) {
      this.clearParentBenefit();
    }
  }

  onBlurFirstAlternativeBenefit() {
    const value = this.benefitLimitsFormGroup.get(Controls.FirstAlternativeBenefit).value;
    if (!value) {
      this.clearFirstAlternativeBenefit();
    }
  }

  onBlurSecondAlternativeBenefit() {
    const value = this.benefitLimitsFormGroup.get(Controls.SecondAlternativeBenefit).value;
    if (!value) {
      this.clearSecondAlternativeBenefit();
    }
  }

  onBlurFixedTo2DecimalPlace(controlName: string) {
    const value = Number(this.benefitLimitsFormGroup.get(controlName).value);
    if (value) {
      const fixed = value.toFixed(2);
      this.benefitLimitsFormGroup.patchValue({[controlName]: fixed});
    } else {
      this.benefitLimitsFormGroup.patchValue({[controlName]: null});
    }
  }

  onBlurTruncateDecimalPart(controlName: string) {
    this.benefitLimitsFormGroup.patchValue({
      [controlName]: Math.trunc(this.benefitLimitsFormGroup.get(controlName).value)
    });
  }

  onSavePBLConfigurations() {

    if (!this.selectedBenefit && this.isAddingNewBenefit) {
      this.sharedService.openSnackBar(`Add benefit before saving configuration`, 'Dismiss');
      return;
    } else {
      if (!this.selectedLimit && this.isAddingNewLimit && !this.isAddingNewBenefit) {
        /*
          Case:
           - Add New Benefit Configuration -> Save Configuration -> Instead of Adding Limit -> Select Benefit
           - Try to save Configuration. We need to restrict user other wise it will result into `null` entry
           - due to DTO link object is saved at Limit Level not at benefit level.
         */
        this.sharedService.openSnackBar(`Add or Delete new limit before saving configuration`, 'Dismiss');
        return;
      } else if (this.selectedBenefit && !this.isAddingNewBenefit) {
        const benefitDTO = this._benefitsDTOHashMap.get(this.selectedBenefit?.benefitId);
        this.updatePBLConfigurations(benefitDTO);

        // User is already restricted above, so we are 100% sure at this point DTO object
        // will be there apart from `null` for benefit that can be filtered out from LIST
        // Comment: This will Remove DTO object with Limit = null.

        this.productBenefitLimitLinkDTOList = this.productBenefitLimitLinkDTOList.filter(item => item.benefit && item.limit);
      } else {
        // Add NEW Benefit DTO link object in LIST
        const benefitDTO = this.selectedBenefit;
        const limitDTO = this.selectedLimit;
        const newProductBenefitLimitLinkDTO = this.getProductBenefitLimitLinkDTOPayload(benefitDTO, limitDTO);
        this.productBenefitLimitLinkDTOList.push(newProductBenefitLimitLinkDTO);
        this.isAddingNewBenefit = false;
        this.isAddingNewLimit = true;
      }
    }

    this.buildAndExpandTree(this.productBenefitLimitLinkDTOList);
    this.checkAndNotifyParentIfFormIsValid();
    this.showConfigurationSection = false;
    this.resetState();
  }

  onFinishAndSaveProduct() {
    /*
     Case when you add benefit YOU are enforcing to add benefit PBL Link DTO so at the time
     of configuration LinkDTO object will have Limit=null. So before sending payload to backend
     you need to remove that LINK DTO will have where LIMIT is null

     Note: THIS IS NOT NEEDED MORE AS WE ARE FILTERING on "onSavePBLConfigurations" method due to
     edge case but in case it is good send payload to backend only which have Benefit and limit both.
     */
    const payload = this.productBenefitLimitLinkDTOList.filter(item => item.benefit && item.limit);
    this.ProductBenefitLimitLinkDTOListChange.emit(payload);
    this.resetState();
  }

  onBenefitNodeClicked(node: ItemFlatNode) {
    if (node.level === this.treeLevel.ProductLevel) {
      return;
    }

    const nestedNode: ItemNode = this.flatNodeMap.get(node);
    this.isBenefitWithoutLimit = nestedNode && nestedNode.children.length === 0;

    const benefitId = this.getBenefitID(nestedNode);
    let pblItemFound;

    if (this.isBenefitWithoutLimit) {
      // Benefit With No Limit
      // Case => Add Benefit => Without Adding limit => Click on Benefit
      // Inorder to populate Benefit Configuration Limit at this point will be null
      pblItemFound = this.productBenefitLimitLinkDTOList?.find(item => item?.limit === null);
    } else {
      pblItemFound = this.productBenefitLimitLinkDTOList?.find(item => item?.benefit?.benefitId === benefitId);
    }

    if (pblItemFound) {
      const {benefit, limit, parentBenefit, firstAlternativeBenefit, secondAlternativeBenefit} = pblItemFound;
      this._benefitsDTOHashMap.set(benefit.benefitId, benefit);
      this.selectedBenefit = benefit;
      this.selectedLimit = limit;
      this.selectedParentBenefit = parentBenefit;
      this.selectedFirstAlternativeBenefit = firstAlternativeBenefit;
      this.selectedSecondAlternativeBenefit = secondAlternativeBenefit;
      this.setPBLConfigurations(pblItemFound);
      this.setOrClearParentBenefitControlValidator(benefit);
    } else {
      this.resetFormGroup();
    }

    this.showConfigurationSection = true;

    if (this.isScrollToTop) {
      this.scrollToTop();
    }
  }

  onBack() {
    this.isAddingNewBenefit = false;
    this.isAddingNewLimit = false;
  }

  onOutsideClick($event) {
    const {target} = $event;
    const ignoreElements = ['configuration-section'];

    if (ignoreElements?.length) {

      // Note: Fetch DOM element of Ignored Element's' and if user click withim
      // the Ignored element just return and ignore Outside click element

      let isClickedWithinIgnoredElement = false;
      ignoreElements.some((element) => {
        const domElement = document.querySelector(`.${element}`);
        if (domElement?.contains(target)) {
          isClickedWithinIgnoredElement = true;
          return;
        }
      });

      if (isClickedWithinIgnoredElement) {
        return;
      }
    }

    if (['mat-icon', 'input', 'span'].includes(target.tagName.toLowerCase())) {
      return;
    }


    const classList = target.classList?.length ? [...target?.classList] : [];
    if (
      classList.includes('mat-calendar-body-cell-content') ||
      classList.includes('mat-calendar-body-selected') ||
      classList.includes('mat-calendar-previous-button') ||
      classList.includes('mat-calendar-next-button')
    ) {
      return;
    }

    this.showConfigurationSection = false;
    this.resetState();
  }

  onCancel() {
    this.showConfigurationSection = false;
    this.resetState();
  }

  private updatePBLConfigurations(benefitDTO: BenefitDTO) {
    const {benefitId} = benefitDTO;
    this.productBenefitLimitLinkDTOList.forEach((item: ProductBenefitLimitLinkDTO) => {
      if (item.benefit.benefitId === benefitId) {
        const {benefit, limit} = item;
        item.created = this.isEditMode ? item.created : new Date();
        item.createdBy = this.isEditMode ? item.createdBy : this.loggedInUserName;
        item.tenantId = this.isEditMode ? item.tenantId : null;
        item.updated = this.isEditMode ? new Date() : null;
        item.updatedBy = this.isEditMode ? this.loggedInUserName : null;
        item.benefit = benefit;
        item.limit = limit;
        item.coPaymentPercentage = this.benefitLimitsFormGroup.get(this.controls.CoPaymentPercentage).value;
        item.deductible = this.benefitLimitsFormGroup.get(this.controls.Deductible).value;
        item.waitingPeriod = this.benefitLimitsFormGroup.get(this.controls.WaitingPeriod).value;
        item.percentageOfPayerRate = this.benefitLimitsFormGroup.get(this.controls.PercentageOfPayerRate).value;
        item.pecWaitingPeriod = this.benefitLimitsFormGroup.get(this.controls.PecWaitingPeriod).value;
        item.startDate = this.benefitLimitsFormGroup.get(this.controls.StartDate).value;
        item.endDate = this.benefitLimitsFormGroup.get(this.controls.EndDate).value;
        item.parentBenefit = this.selectedParentBenefit;
        item.firstAlternativeBenefit = this.selectedFirstAlternativeBenefit;
        item.secondAlternativeBenefit = this.selectedSecondAlternativeBenefit;
      }
    });
  }

  // Number Type just to return -1
  private getBenefitID(item: ItemNode): bigint | number {
    if (item.benefitId) {
      return item.benefitId;
    } else if (item?.children?.length) { // Get benefit from any child
      return item.children[0].benefitId;
    }
    return -1;
  }

  private setPBLConfigurations(item: ProductBenefitLimitLinkDTO) {
    if (item) {
      this.benefitLimitsFormGroup.patchValue({
        coPaymentPercentage: formatNumber(item.coPaymentPercentage),
        deductible: formatNumber(item.deductible),
        waitingPeriod: item.waitingPeriod,
        percentageOfPayerRate: formatNumber(item.percentageOfPayerRate),
        pecWaitingPeriod: item.pecWaitingPeriod,
        startDate: item.startDate ? new Date(Number(item.startDate)) : null,
        endDate: item.endDate ? new Date(Number(item.endDate)) : null,
        parentBenefit: item?.parentBenefit ? this.getBenefitDisplayName(item.parentBenefit, false) : null,
        firstAlternativeBenefit: item?.firstAlternativeBenefit ? this.getBenefitDisplayName(item.firstAlternativeBenefit, false) : null,
        secondAlternativeBenefit: item?.secondAlternativeBenefit ? this.getBenefitDisplayName(item.secondAlternativeBenefit, false) : null
      });
    }
  }

  private checkIsEmptyBenefitOrLimitAlreadyExists(node: ItemFlatNode) {
    const parentNode = this.flatNodeMap.get(node);
    if (node.level === this.treeLevel.ProductLevel) {
      //  Adding new Benefit:
      if (this.isEmptyItemExists(parentNode.children)) {
        this.sharedService.openSnackBar(`Save previous benefit`, 'Dismiss');
        return true;
      }

    } else if (node.level === this.treeLevel.BenefitLevel) {
      //  Adding new Limit:
      if (this.isEmptyItemExists(parentNode.children)) {
        this.sharedService.openSnackBar(`Save previous limit`, 'Dismiss');
        return true;
      }
    }
    return false;
  }

  private checkBenefitConfigurationsMustExistInListBeforeAddingLimits(node: ItemFlatNode): boolean {
    //TODO: remove this method since it has no usages
    if (this.productBenefitLimitLinkDTOList?.length) {
      const splitByColon = node.item.split(':'); // <benefitCode: <benefitName>
      return this.productBenefitLimitLinkDTOList.some(item => {
        let name;
        if (splitByColon.length > 1) {
          name = splitByColon[1]; // Case: Benefit Code + Name both exists
        } else if (splitByColon.length === 1) {
          name = splitByColon[0]; // Case: Benefit Code not exists
        }
        return name.trim() === item?.benefit?.name.trim();
      });
    }
    return false;
  }

  private deleteNode(node: ItemFlatNode) {
    const parentNode = this.getParentNode(node);
    const parentFlat = this.flatNodeMap.get(parentNode);

    if (parentFlat.children.length === 1 && node.level === TreeLevel.LimitLevel) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '40vw',
        data: 'Are you sure you want to delete last limit?. Benefit will be delete too and its configuration'
      });
      dialogRef.afterClosed()
        .subscribe((isConfirmed) => {
          if (isConfirmed) {
            this.pblTreeService.deleteItem(parentFlat, node.item);
            this.treeControl.expand(node);
            this.deletePBLConfigurations(node);
            this.resetState();
            this.buildAndExpandTree(this.productBenefitLimitLinkDTOList);
          }
        });
    } else {
      this.pblTreeService.deleteItem(parentFlat, node.item);
      this.treeControl.expand(node);
      this.deletePBLConfigurations(node);
      this.resetState();
    }

    if (node.level === TreeLevel.BenefitLevel) {
      this.isAddingNewBenefit = false;
    } else if (node.level === TreeLevel.LimitLevel) {
      this.isAddingNewLimit = false;
    }

    this.isBenefitWithoutLimit = parentFlat && parentFlat.children.length === 0;
    this.showConfigurationSection = false;
    this.checkAndNotifyParentIfFormIsValid();
  }

  private deletePBLConfigurations(flatNode) {
    if (flatNode.level === this.treeLevel.BenefitLevel) {

      const flattenedNode = this.flatNodeMap.get(flatNode);
      let benefitId = null;
      if (!flattenedNode?.children?.length) {
        /*
         - Empty Benefit With no children
         - LinkDTO object is always added at Limit Level so simple return is fine
         */
        return;
      }

      benefitId = flattenedNode.children[0].benefitId; // GET first child `benefitId`
      this.productBenefitLimitLinkDTOList = this.productBenefitLimitLinkDTOList
        .filter((item: ProductBenefitLimitLinkDTO) => item.benefit.benefitId !== benefitId);
    } else if (flatNode.level === this.treeLevel.LimitLevel) {
      const flattenedNode = this.flatNodeMap.get(flatNode);
      const benefitId = flattenedNode?.benefitId;
      const limitId = flattenedNode?.limitId;

      const limitIndex = this.productBenefitLimitLinkDTOList.findIndex(item => {
        return (item.benefit.benefitId === benefitId && item.limit.limitId === limitId);
      });
      if (limitIndex > -1) {
        this.productBenefitLimitLinkDTOList.splice(limitIndex, 1);
      }
    }
  }

  private buildAndExpandTree(_data: any[]) {
    const sortedData = this.sortParentBenefitByBenefitCode(_data);
    const treeData = this.processProductBenefitLimitLinkDTOTree(sortedData);     // Build the tree nodes from JSON object.
    const data = this.pblTreeService.buildFileTree(treeData, 0);
    this.pblTreeService.dataChange.next(data); // Notify the change.
    this.handleTreeExpansionToBenefitLevel();
  }

  private sortParentBenefitByBenefitCode(data: ProductBenefitLimitLinkDTO[]): ProductBenefitLimitLinkDTO[] {
    // Sort descending so that messages are order by rejected, pended then benefit
    let sortedList: ProductBenefitLimitLinkDTO[];
    sortedList = data
      .sort((obj1: ProductBenefitLimitLinkDTO, obj2: ProductBenefitLimitLinkDTO) => {
        if (obj1.parentBenefit?.benefitCode < obj2.parentBenefit?.benefitCode) {
          return -1;
        } else if (obj1.parentBenefit?.benefitCode > obj2.parentBenefit?.benefitCode) {
          return 1;
        } else {
          return this.sortByBenefitCode(obj1, obj2);
        }
      })
      .sort((obj1: ProductBenefitLimitLinkDTO, obj2: ProductBenefitLimitLinkDTO) => {
        if (obj1.parentBenefit?.benefitCode === obj2.parentBenefit?.benefitCode) {
          return this.sortByBenefitCode(obj1, obj2);
        }
        if (this.PRIORITY_BENEFIT_CODES.includes(obj1.parentBenefit?.benefitCode)) {
          return -1;
        }
        if (this.PRIORITY_BENEFIT_CODES.includes(obj2.parentBenefit?.benefitCode)) {
          return -1;
        }
      });
    return sortedList;
  }

  private sortByBenefitCode(obj1: ProductBenefitLimitLinkDTO, obj2: ProductBenefitLimitLinkDTO) {
    if (obj1.benefit?.benefitCode < obj2.benefit?.benefitCode) {
      return -1;
    } else if (obj1.benefit?.benefitCode > obj2.benefit?.benefitCode) {
      return 1;
    } else {
      return 0;
    }
  }

  private setBenefitLimitDTOHashmapValues(_data: any[]) {
    _data.forEach((item: ProductBenefitLimitLinkDTO) => {
      const {benefit, limit} = item;

      if (!this._benefitsDTOHashMap.has(benefit.benefitId)) {
        this._benefitsDTOHashMap.set(benefit.benefitId, benefit);
      }
      if (!this._limitsDTOHashMap.has(limit.limitId)) {
        this._limitsDTOHashMap.set(limit.limitId, limit);
      }
    });
  }

  private processProductBenefitLimitLinkDTOTree(data: ProductBenefitLimitLinkDTO[]): object {

    let treeData = {
      [this.productDTO.name]: {}
    };

    if (data && data?.length) {
      data.forEach((item: ProductBenefitLimitLinkDTO) => {
        const {limit} = item;
        const benefitName = this.getBenefitNameByParentBenefitCode(item);
        const limitName = this.getLimitDisplayName(limit);
        const productName = this.productDTO.name;
        treeData[productName] = {
          ...treeData[productName],
          [benefitName]: {
            ...treeData[productName][benefitName],
            [limitName]: {
              benefitId: item.benefit.benefitId,
              limitId: item.limit ? item.limit.limitId : null
            }
          }
        };
      });
      treeData = this.removeNullEntriesAtBenefitLevel(treeData, this.productDTO.name);
      return treeData;
    }
    return treeData;
  }

  /**
   *
   * @param data
   * @param productName

   * Note:
   * Case: Benefit configurations are saved, and We need to rearrange benefit by parent benefit
   *  when save is clicked, the benefit should immediately have the display string updated to
   *  “parentBenefitCode -> benefitCode: benefitName“ and should be re-ordered
   */
  private removeNullEntriesAtBenefitLevel(data, productName) {
    const treeData = JSON.parse(JSON.stringify(data));
    const benefitLevelData = treeData[productName];

    Object.keys(benefitLevelData)
      .forEach(benefitKey => {
          Object.keys(benefitLevelData[benefitKey]).forEach(limitKey => {
            if (Object.keys(benefitLevelData[benefitKey]).includes('null')) {
              delete benefitLevelData[benefitKey][limitKey];
            }
          });
        }
      );
    return treeData;
  }

  private getBenefitNameByParentBenefitCode(item: ProductBenefitLimitLinkDTO) {
    const {parentBenefit, benefit} = item;
    const parentBenefitCode = parentBenefit?.benefitCode ?? '';

    if (item && this.PRIORITY_BENEFIT_CODES.includes(benefit.benefitCode)) {
      return `${this.getBenefitDisplayName(benefit)}`; // Parent: Benefit ID not required when "Benefit Code" is "OAB001"
    } else if (item) {
      return `${this.getBenefitDisplayName(benefit)} (Parent: <b>${parentBenefitCode || 'None'}</b>)`;
    }
    return null;
  }

  private handleTreeExpansionToBenefitLevel() {
    this.pblTreeService
      .dataChangeAsObservable()
      .pipe(
        take(1)
      )
      .subscribe(data => {
        if (data.length > 0) {
          const node = this.nestedNodeMap.get(data[0]); // Gets FlatNode
          this.treeControl.expand(node);
        }
      });
  }

  private getFilteredListOfBenefits(searchValue: string): void {

    const byBenefitNamePagedRequestDTO =
      EntRequestBuilder.getTblBenefitsPagedRequestDTO({
        filterValue: searchValue,
      });

    const byBenefitCodePagedRequestDTO =
      EntRequestBuilder.getTblBenefitsPagedRequestDTO({
        filterField: 'benefitCode',
        filterValue: searchValue,
      });

    forkJoin(
      this.benefitService.getListOfBenefits(byBenefitNamePagedRequestDTO),
      this.benefitService.getListOfBenefits(byBenefitCodePagedRequestDTO)
    )
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe((
          responses: [
            PagedResponseDTO<BenefitDTO>,
            PagedResponseDTO<BenefitDTO>
          ]) => {
          this.filteredBenefits = responses[0].resultList.concat(responses[1].resultList);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getFilteredListOfLimits(searchValue: string): void {
    const pagedRequestDTO = EntRequestBuilder.getFilteredListOfLimitsPagedRequestDTO(searchValue);
    this.limitService.getListOfLimits(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(
        (response: PagedResponseDTO<LimitDTO>) => {
          this.filteredLimits = response.resultList;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getProductBenefitLimitLinkDTOPayload(benefitDTO: BenefitDTO, limitDTO: LimitDTO) {
    let payload: ProductBenefitLimitLinkDTO;
    payload = {
      created: this.isEditMode ? this.productDTO.created : new Date(),
      createdBy: this.isEditMode ? this.productDTO.createdBy : this.loggedInUserName,
      tenantId: this.isEditMode ? this.productDTO.tenantId : null,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null,
      benefit: benefitDTO,
      limit: limitDTO,
      coPaymentPercentage: this.benefitLimitsFormGroup.get(this.controls.CoPaymentPercentage).value,
      deductible: this.benefitLimitsFormGroup.get(this.controls.Deductible).value,
      waitingPeriod: this.benefitLimitsFormGroup.get(this.controls.WaitingPeriod).value,
      percentageOfPayerRate: this.benefitLimitsFormGroup.get(this.controls.PercentageOfPayerRate).value,
      pecWaitingPeriod: this.benefitLimitsFormGroup.get(this.controls.PecWaitingPeriod).value,
      startDate: this.benefitLimitsFormGroup.get(this.controls.StartDate).value,
      endDate: this.benefitLimitsFormGroup.get(this.controls.EndDate).value,
      parentBenefit: this.selectedParentBenefit,
      firstAlternativeBenefit: this.selectedFirstAlternativeBenefit,
      secondAlternativeBenefit: this.selectedSecondAlternativeBenefit
    };
    return payload;
  }

  private resetState() {
    this.benefitLimitsFormGroup.reset();
    this.selectedBenefit = null;
    this.selectedLimit = null;
    this.selectedParentBenefit = null;
    this.selectedFirstAlternativeBenefit = null;
    this.selectedSecondAlternativeBenefit = null;
    this.filteredLimits = [];
    this.filteredBenefits = [];
  }

  private isEmptyItemExists(children: ItemNode[]) {
    return children && children.some((child) => child.item === this.EMPTY_VALUE);
  }

  private setOrClearParentBenefitControlValidator(benefit: BenefitDTO) {
    // Case: Parent: Benefit ID not required when "Benefit Code" is "OAB001"
    if (this.PRIORITY_BENEFIT_CODES.includes(benefit?.benefitCode)) {
      this.sharedFormService.clearValidator(this.benefitLimitsFormGroup, Controls.ParentBenefit);
      this.isParentBenefitRequired = false;
      this.benefitLimitsFormGroup.get(Controls.ParentBenefit).disable();
    } else {
      this.sharedFormService.setValidator(this.benefitLimitsFormGroup, Controls.ParentBenefit, [Validators.required]);
      this.isParentBenefitRequired = true;
      this.benefitLimitsFormGroup.get(Controls.ParentBenefit).enable();
    }
  }

  private checkAndNotifyParentIfFormIsValid() {
    if (this.productBenefitLimitLinkDTOList?.length) {
      this.isValid.next(true);
    } else {
      this.isValid.next(false);
    }
  }

  private scrollToTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }
}
